import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Brain, BookOpen, ChevronRight, ImageIcon, GraduationCap, Target, Lock, Award, Globe, Sparkles, Beaker, Calculator, Stethoscope, Shield, Briefcase, Tag, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import Footer from './Footer';
import FeaturedCarousel from './FeaturedCarousel';
import FreeCreditsBanner from './FreeCreditsBanner';
import axios from 'axios';
import { isMobileApp } from '../utils/platform';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Course cache for instant category page loading
const courseCache = {
  courses: null,
  timestamp: null,
  CACHE_DURATION: 5 * 60 * 1000 // 5 minutes
};

// Export for use in CategoryCourses
export const getCachedCourses = () => {
  if (courseCache.courses && courseCache.timestamp && 
      (Date.now() - courseCache.timestamp) < courseCache.CACHE_DURATION) {
    return courseCache.courses;
  }
  return null;
};

export const setCachedCourses = (courses) => {
  courseCache.courses = courses;
  courseCache.timestamp = Date.now();
};

import SEOHead from './SEOHead';

const Dashboard = () => {
  const { user, openAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [ambitionText, setAmbitionText] = useState('');

  // Pre-fetch courses for instant category page loading
  useEffect(() => {
    const prefetchCourses = async () => {
      // Only fetch if cache is empty or expired
      if (!getCachedCourses()) {
        try {
          const response = await axios.get(`${API}/api/lms/courses?limit=100`);
          if (response.data.success) {
            const allCourses = response.data.data.courses || [];
            setCachedCourses(allCourses);
          }
        } catch (error) {
          console.error('Failed to prefetch courses:', error);
        }
      }
    };
    
    prefetchCourses();
  }, []);

  // Handle Guide Me button click
  const handleGuideMe = () => {
    if (!ambitionText.trim()) return;
    
    // If user is not logged in, ask them to login
    if (!user) {
      openAuth('register');
      return;
    }
    
    // Navigate to career ambition page with the ambition text
    navigate('/career-ambition', { state: { initialAmbition: ambitionText.trim() } });
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGuideMe();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-orange-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Career Tools with ORIGINAL titles/descriptions
  const careerTools = [
    {
      id: "ask-nerd-ai",
      icon: Brain,
      iconBg: "bg-indigo-500",
      textColor: "text-indigo-600",
      title: "Career Advisor AI",
      description: "Get Career Advice and Clear Your Subject Doubts",
      action: () => navigate('/ai-advisor'),
      testId: "dashboard-ai-advisor",
      image: "https://customer-assets.emergentagent.com/job_736cdda7-e974-4fad-aa62-3a758e69e883/artifacts/780abfwk_Time%288%29.png",
      objectPosition: "object-top"
    },
    {
      id: "research-ai",
      icon: Beaker,
      iconBg: "bg-blue-500",
      textColor: "text-blue-600",
      title: "Research AI",
      description: "Understand Published Research Papers and Design Research Proposals",
      action: () => navigate('/learn-research'),
      testId: "dashboard-learn-research",
      image: "https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/6l4yef92_6.png",
      objectPosition: "object-[center_20%]"
    },
    {
      id: "creator",
      icon: ImageIcon,
      iconBg: "bg-purple-500",
      textColor: "text-purple-600",
      title: "Creator AI",
      description: "Learn to create Social Media content, Avatars, Reels and Videos using Creator AI",
      action: () => navigate('/image-generator'),
      testId: "dashboard-image-generator",
      image: "https://customer-assets.emergentagent.com/job_736cdda7-e974-4fad-aa62-3a758e69e883/artifacts/2eq1wwji_Time%2815%29.jpg",
      objectPosition: "object-top"
    },
    {
      id: "global-scholarships",
      icon: GraduationCap,
      iconBg: "bg-amber-500",
      textColor: "text-amber-600",
      title: "Global Scholarships",
      description: "Find all Bachelors, MS & PhD Scholarships in UK, USA, Canada, Europe, Australia, New Zealand, Japan, Korea and Singapore.",
      action: () => navigate('/scholarships'),
      testId: "dashboard-global-scholarships",
      image: "https://customer-assets.emergentagent.com/job_936c1473-1350-488d-a877-d3aeedf41e88/artifacts/1scgvhaw_Student%20Offer%20Zone%20%21%281%29.png",
      objectPosition: "object-top"
    }
  ];

  // Exam Banks — Tab 1: Indian / General
  const indianExamBanks = [
    {
      id: "free-trial",
      icon: Target,
      iconBg: "bg-pink-500",
      textColor: "text-pink-600",
      title: "Free To Try all the Exams !",
      description: "Exams: PLAB, USMLE, AMC, DHA, ADC, ORE, NAPLEX, PEBC",
      action: () => navigate('/free-trial-exams'),
      testId: "dashboard-free-trial",
      badge: "FREE",
      image: "https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/m4czax2x_5.png"
    },
    {
      id: "global",
      icon: Globe,
      iconBg: "bg-green-500",
      textColor: "text-green-600",
      title: "Go Global with GRE & GMAT",
      description: "GRE and GMAT question banks for global aspirants",
      action: () => navigate('/global-exam-bank'),
      testId: "dashboard-global-exams",
      image: "https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/8ccg7rqz_1.png"
    },
    {
      id: "engineering",
      icon: Calculator,
      iconBg: "bg-orange-500",
      textColor: "text-orange-600",
      title: "IIT-JEE & NEET-UG : Success Bank",
      description: "Your Last Minute Success Prep for IIT-JEE & NEET-UG Exam",
      action: () => navigate('/engineering-exam-bank'),
      testId: "dashboard-engineering-bank",
      image: "https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/0drmeg5z_3.png"
    }
  ];

  // Exam Banks — Tab 2: International Medical & Pharma
  const internationalExamBanks = [
    {
      id: "medical",
      icon: Stethoscope,
      iconBg: "bg-emerald-500",
      textColor: "text-emerald-600",
      title: "Doctors : Exam Success Bank",
      description: "Medical: PLAB, USMLE, AMC, MCCQE, DHA & More",
      action: () => navigate('/exam-bank'),
      testId: "dashboard-exam-bank",
      image: "https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/o8hnj7cc_2.png"
    },
    {
      id: "dental",
      icon: BookOpen,
      iconBg: "bg-cyan-500",
      textColor: "text-cyan-600",
      title: "Dentist Global Success Bank",
      description: "Dental: DHA Dental, ADC Part 1, ORE Part 1, INBDE",
      action: () => navigate('/dentist-exam-bank'),
      testId: "dashboard-dentist-bank",
      image: "https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/updk27gm_8.png"
    },
    {
      id: "pharmacy",
      icon: BookOpen,
      iconBg: "bg-teal-500",
      textColor: "text-teal-600",
      title: "International Pharmacist Success Bank",
      description: "Pharmacy: NAPLEX, MPJE, PEBC, GPhC, OPRA, CAP, DHA, SPLE",
      action: () => navigate('/pharmacy-exam-bank'),
      testId: "dashboard-pharmacy-bank",
      image: "https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/04vx0zh6_4.png"
    },
  ];

  // Other tools with ORIGINAL titles/descriptions
  const otherTools = [
    {
      id: "pricing",
      icon: Award,
      iconBg: "bg-amber-500",
      textColor: "text-amber-600",
      title: "Price Plans",
      description: "View our affordable subscription plans and choose your success path",
      action: () => navigate('/pricing'),
      testId: "dashboard-pricing",
      image: "https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/5bu193dk_7.png",
      objectPosition: "object-top"
    }
  ];

  // SQUARE card - Mobile-first responsive design
  const SquareFeatureCard = ({ item }) => {
    const Icon = item.icon;

    return (
      <div 
        onClick={item.locked ? undefined : item.action}
        className={`${item.locked ? 'opacity-60' : 'cursor-pointer hover:scale-[1.02] hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] active:scale-95'} 
          bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-500 border border-white/8 hover:border-gold-primary/30 aspect-square flex flex-col touch-manipulation`}
        data-testid={item.testId}
      >
        {/* Image Container - 75% of card height */}
        <div className="relative flex-[75] overflow-hidden">
          <img 
            src={item.image} 
            alt={item.title}
            className={`w-full h-full object-cover ${item.objectPosition || 'object-center'} transition-transform duration-700 group-hover:scale-110`}
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%231111118" width="200" height="200"/></svg>';
            }} loading="lazy" decoding="async" />
          
          {/* Icon badge - Mobile-first sizing */}
          <div className={`absolute top-2 left-2 sm:top-3 sm:left-3 w-10 h-10 sm:w-12 sm:h-12 ${item.iconBg} rounded-xl flex items-center justify-center shadow-lg`}>
            <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          
          {/* FREE badge - Mobile-first */}
          {item.badge && (
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-pink-500 text-white text-xs sm:text-sm font-bold px-2 py-1 sm:px-2.5 sm:py-1 rounded-lg shadow-lg">
              {item.badge}
            </div>
          )}
          
          {/* Lock overlay */}
          {item.locked && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 sm:p-4">
                <Lock className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
            </div>
          )}
        </div>
        
        {/* Text Section - Mobile-first padding */}
        <div className="flex-[25] p-3 sm:p-4 bg-[#0a0e17] border-t border-white/10 flex flex-col justify-center">
          <h3 className="text-[11px] sm:text-lg md:text-xl font-bold gradient-text-gold mb-0.5 sm:mb-1 font-outfit leading-tight">{item.title}</h3>
          <p className="text-[9px] sm:text-sm md:text-base text-white font-outfit line-clamp-2 leading-snug">{item.description}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <SEOHead
        title="Dashboard"
        description="Your personalised Nerd Nation dashboard — AI career advisor, exam preparation, global scholarships, student jobs and community tools all in one place."
        path="/home"
        noIndex={true}
      />
      <FreeCreditsBanner />

      {/* Welcome Header */}
      <div className="w-full px-4 pt-6 pb-4 sm:px-6 sm:pt-8 sm:pb-4 md:max-w-6xl md:mx-auto text-center">
        <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 leading-tight">
          <span className="text-white">Welcome to </span>
          <span className="gradient-text-gold">Nerd Nation</span>
        </h1>
        <p className="font-outfit text-sm sm:text-base md:text-lg text-gray-400 font-medium px-4">
          You Can Achieve Any Ambition With Us !
        </p>
      </div>

      {/* Featured Carousel — full width below title */}
      <div className="w-full mb-6">
        <FeaturedCarousel />
      </div>

      {/* Rest of page content */}
      <div className="w-full px-4 py-2 sm:px-6 md:max-w-6xl md:mx-auto">
        <section className="mb-8 sm:mb-10 md:mb-12">
          <Card 
            className="bg-[#0a0e17] backdrop-blur-xl border border-white shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden hover:border-gold-primary/30 transition-all duration-500"
            data-testid="dashboard-ai-advisor-career-ambition"
          >
            <CardContent className="p-5 sm:p-6 md:p-8">
              {/* Heading - Mobile-first sizes */}
              <div className="mb-5 sm:mb-6 text-center sm:text-left">
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold gradient-text-gold mb-2 sm:mb-3 font-outfit">
                  What is Your Ambition?
                </h2>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed font-outfit">
                  I will tell you how to achieve it !
                </p>
              </div>
              
              {/* Input Area - Mobile-first touch targets */}
              <div className="space-y-4 sm:space-y-5 md:space-y-6">
                <div className="relative">
                  <textarea
                    placeholder="My ambition is to become a..."
                    value={ambitionText}
                    onChange={(e) => setAmbitionText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleGuideMe();
                      }
                    }}
                    rows={4}
                    className="w-full p-4 sm:p-5 md:p-6 text-base sm:text-lg border-2 border-white focus:border-gold-primary focus:outline-none focus:ring-4 focus:ring-gold-primary/20 rounded-xl bg-[#0a0a10]/60 backdrop-blur-sm shadow-inner text-white placeholder:text-gray-500 resize-none font-outfit transition-all duration-300"
                    data-testid="ambition-input"
                  />
                </div>
                
                {/* Keep desktop layout on mobile: Ambition AI left, Guide Me button right */}
                <div className="flex flex-row items-center justify-between gap-3 sm:gap-4">
                  <button
                    onClick={() => navigate('/career-ambition')}
                    className="group cursor-pointer focus:outline-none text-left"
                  >
                    <h3 className="text-lg sm:text-xl font-bold text-white hover:scale-105 transition-all duration-200 font-outfit">
                      Ambition AI
                    </h3>
                  </button>
                  
                  {/* Guide Me button - right aligned on both mobile and desktop */}
                  <Button
                    onClick={handleGuideMe}
                    disabled={!ambitionText.trim()}
                    className={`px-6 sm:px-10 py-2.5 sm:py-4 md:py-5 text-sm sm:text-base md:text-lg rounded-lg sm:rounded-xl font-semibold transition-all duration-300 shadow-lg disabled:opacity-50 font-outfit ${
                      ambitionText.trim() 
                        ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white hover:shadow-[0_12px_48px_rgba(251,146,60,0.6)] hover:scale-105 active:scale-95' 
                        : 'bg-transparent border-2 border-white/20 text-gray-500 cursor-not-allowed'
                    }`}
                    data-testid="guide-me-button"
                  >
                    Guide Me
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Career Tools Section - Mobile-first: Single column, then grid */}
        <section className="mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-5 sm:mb-6 flex items-center justify-start gap-2 sm:gap-3 font-playfair">
            <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-gold-primary flex-shrink-0" />
            <span>Career Tools</span>
          </h2>
          {/* Mobile: 2 columns, Desktop: 3 columns */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
            {careerTools.map((item) => (
              <SquareFeatureCard key={item.id} item={item} />
            ))}
          </div>
        </section>

        {/* Protect Reality Section - Mobile-first */}
        <section className="mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-5 sm:mb-6 flex items-center justify-start gap-2 sm:gap-3 font-playfair">
            <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-400 flex-shrink-0" />
            <span>Protect Reality</span>
          </h2>
          {/* Mobile: 2 columns, Desktop: 3 columns */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
            <div 
              onClick={() => navigate('/samhara-ai')}
              className="group cursor-pointer hover:scale-[1.02] hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-500 border border-white/8 hover:border-gold-primary/30 aspect-square flex flex-col active:scale-95"
            >
              {/* Image Container - 75% of card height */}
              <div className="relative flex-[75] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_c701b085-8724-4035-9aff-92f41ba1c7ce/artifacts/ihjrv1oj_Time%2813%29.jpg"
                  alt="Samhara AI"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" decoding="async"/>
                
                {/* Icon badge */}
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 w-10 h-10 sm:w-12 sm:h-12 bg-yellow-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                
              </div>
              
              {/* WHITE Background Text Section - 25% of card height */}
              <div className="flex-[25] p-3 sm:p-4 bg-white flex flex-col justify-center">
                <h3 className="text-[11px] sm:text-lg md:text-xl font-bold text-gray-900 mb-1">Samhara AI</h3>
                <p className="text-[9px] sm:text-sm md:text-base text-gray-600">Detects Deepfake Pictures and Videos !</p>
              </div>
            </div>
          </div>
        </section>

        {/* Success Plans Section - 4 Category Cards */}
        <section className="mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-5 sm:mb-6 flex items-center justify-start gap-2 sm:gap-3 font-playfair">
            <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6 text-purple-400 flex-shrink-0" />
            <span>Job Pass - Success Plans for Students</span>
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
            {/* Engineering & IT Success Plan */}
            <div 
              onClick={() => navigate('/category/engineering-it')}
              className="group cursor-pointer hover:scale-[1.02] hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-500 border border-white/8 hover:border-gold-primary/30 aspect-square flex flex-col active:scale-95"
              data-testid="category-engineering-it"
            >
              <div className="relative flex-[75] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_5c37646e-f49e-46e8-8d42-15b44dca3659/artifacts/m577va6a_12.jpg" 
                  alt="Engineering & IT Success Plan"
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" loading="lazy" decoding="async"/>
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 w-10 h-10 sm:w-12 sm:h-12 bg-blue-700 rounded-xl flex items-center justify-center shadow-lg">
                  <Calculator className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
              <div className="flex-[25] p-3 sm:p-4 bg-[#0a0e17] border-t border-white/10 flex flex-col justify-center">
                <h3 className="text-[11px] sm:text-lg md:text-xl font-bold gradient-text-gold mb-0.5 sm:mb-1 font-outfit leading-tight">Engineering & IT</h3>
                <p className="text-[9px] sm:text-sm md:text-base text-white font-outfit line-clamp-2 leading-snug">BTech, MTech, BCA, MCA & BSc Computer Science & Statistics Students - Get Access to All Top Courses For 1 Year !</p>
              </div>
            </div>

            {/* Finance & Management Success Plan */}
            <div 
              onClick={() => navigate('/category/finance-management')}
              className="group cursor-pointer hover:scale-[1.02] hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-500 border border-white/8 hover:border-gold-primary/30 aspect-square flex flex-col active:scale-95"
              data-testid="category-finance-management"
            >
              <div className="relative flex-[75] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_5c37646e-f49e-46e8-8d42-15b44dca3659/artifacts/c7bnkqas_9.jpg" 
                  alt="Finance & Management Success Plan"
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" loading="lazy" decoding="async"/>
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 w-10 h-10 sm:w-12 sm:h-12 bg-emerald-700 rounded-xl flex items-center justify-center shadow-lg">
                  <Target className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
              <div className="flex-[25] p-3 sm:p-4 bg-[#0a0e17] border-t border-white/10 flex flex-col justify-center">
                <h3 className="text-[11px] sm:text-lg md:text-xl font-bold gradient-text-gold mb-0.5 sm:mb-1 font-outfit leading-tight">Finance & Management</h3>
                <p className="text-[9px] sm:text-sm md:text-base text-white font-outfit line-clamp-2 leading-snug">BCom, MCom, BBM, BBA, MBA Students - Get Access to All Top Courses For 1 Year !</p>
              </div>
            </div>

            {/* Pharma & Life-Sciences Success Plan */}
            <div 
              onClick={() => navigate('/category/pharma-lifesciences')}
              className="group cursor-pointer hover:scale-[1.02] hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-500 border border-white/8 hover:border-gold-primary/30 aspect-square flex flex-col active:scale-95"
              data-testid="category-pharma-lifesciences"
            >
              <div className="relative flex-[75] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_5c37646e-f49e-46e8-8d42-15b44dca3659/artifacts/jig6oqk2_11.jpg" 
                  alt="Pharma & Life-Sciences Success Plan"
                  className="w-full h-full object-cover object-[center_20%] transition-transform duration-700 group-hover:scale-110" loading="lazy" decoding="async"/>
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 w-10 h-10 sm:w-12 sm:h-12 bg-purple-700 rounded-xl flex items-center justify-center shadow-lg">
                  <Beaker className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
              <div className="flex-[25] p-3 sm:p-4 bg-[#0a0e17] border-t border-white/10 flex flex-col justify-center">
                <h3 className="text-[11px] sm:text-lg md:text-xl font-bold gradient-text-gold mb-0.5 sm:mb-1 font-outfit leading-tight">Pharma & Life-Sciences</h3>
                <p className="text-[9px] sm:text-sm md:text-base text-white font-outfit line-clamp-2 leading-snug">BPharma, MPharma, PharmaD, MSc Biotech, Microbiology, Life-Sciences Students - Get Access to All Top Courses For 1 Year !</p>
              </div>
            </div>

            {/* Healthcare Success Plan */}
            <div 
              onClick={() => navigate('/category/healthcare')}
              className="group cursor-pointer hover:scale-[1.02] hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-500 border border-white/8 hover:border-gold-primary/30 aspect-square flex flex-col active:scale-95"
              data-testid="category-healthcare"
            >
              <div className="relative flex-[75] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_5c37646e-f49e-46e8-8d42-15b44dca3659/artifacts/ygaxzvmc_Time%289%29.jpg" 
                  alt="Healthcare Success Plan"
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" loading="lazy" decoding="async"/>
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 w-10 h-10 sm:w-12 sm:h-12 bg-red-700 rounded-xl flex items-center justify-center shadow-lg">
                  <Stethoscope className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
              <div className="flex-[25] p-3 sm:p-4 bg-[#0a0e17] border-t border-white/10 flex flex-col justify-center">
                <h3 className="text-[11px] sm:text-lg md:text-xl font-bold gradient-text-gold mb-0.5 sm:mb-1 font-outfit leading-tight">Healthcare</h3>
                <p className="text-[9px] sm:text-sm md:text-base text-white font-outfit line-clamp-2 leading-snug">Excel in medical professional development</p>
              </div>
            </div>
          </div>
        </section>

        {/* Exam Banks Section */}
        <section className="mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-5 sm:mb-6 flex items-center justify-start gap-2 sm:gap-3 font-playfair">
            <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-blue-400 flex-shrink-0" />
            <span>Exam Preparation</span>
          </h2>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
            {/* Free Trial card - FIRST in sequence */}
            <SquareFeatureCard item={indianExamBanks[0]} />
            
            {/* Indian Exams card - SECOND - Dark theme */}
            <div
              onClick={() => navigate('/exam-preparation/indian')}
              data-testid="exam-tab-indian"
              className="group cursor-pointer hover:scale-[1.02] hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-500 border border-white/8 hover:border-gold-primary/30 aspect-square flex flex-col active:scale-95"
            >
              <div className="relative flex-[75] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/0drmeg5z_3.png"
                  alt="Indian Exams"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" decoding="async"/>
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 w-10 h-10 sm:w-12 sm:h-12 bg-orange-700 rounded-xl flex items-center justify-center shadow-lg">
                  <Calculator className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
              <div className="flex-[25] p-3 sm:p-4 bg-[#0a0e17] border-t border-white/10 flex flex-col justify-center">
                <h3 className="text-[11px] sm:text-lg md:text-xl font-bold gradient-text-gold mb-0.5 sm:mb-1 font-outfit leading-tight">Indian Exams</h3>
                <p className="text-[9px] sm:text-sm md:text-base text-white font-outfit line-clamp-2 leading-snug">IIT-JEE, NEET-UG & more</p>
              </div>
            </div>

            {/* International Medical & Pharma card - THIRD - Dark theme */}
            <div
              onClick={() => navigate('/exam-preparation/international')}
              data-testid="exam-tab-international"
              className="group cursor-pointer hover:scale-[1.02] hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-500 border border-white/8 hover:border-gold-primary/30 aspect-square flex flex-col active:scale-95"
            >
              <div className="relative flex-[75] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_learnsphere-105/artifacts/o8hnj7cc_2.png"
                  alt="International Medical & Pharma Exams"
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" loading="lazy" decoding="async"/>
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 w-10 h-10 sm:w-12 sm:h-12 bg-emerald-700 rounded-xl flex items-center justify-center shadow-lg">
                  <Stethoscope className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
              <div className="flex-[25] p-3 sm:p-4 bg-[#0a0e17] border-t border-white/10 flex flex-col justify-center">
                <h3 className="text-[11px] sm:text-lg md:text-xl font-bold gradient-text-gold mb-0.5 sm:mb-1 font-outfit leading-tight">International Medical & Pharma Exams</h3>
                <p className="text-[9px] sm:text-sm md:text-base text-white font-outfit line-clamp-2 leading-snug">International Exams for Medical Doctors, Dental Doctors and Pharmacists</p>
              </div>
            </div>
          </div>
        </section>

        {/* Offer Zone Section */}
        <section className="mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-5 sm:mb-6 flex items-center justify-start gap-2 sm:gap-3 font-playfair">
            <Tag className="h-5 w-5 sm:h-6 sm:w-6 text-orange-400 flex-shrink-0" />
            <span>Offer Zone</span>
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
            {/* Student Offers card */}
            <div
              onClick={() => navigate('/student-offers')}
              data-testid="student-offers-card"
              className="group cursor-pointer hover:scale-[1.02] bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-500 border border-white/8 hover:border-orange-500/30 hover:shadow-[0_20px_60px_rgba(249,115,22,0.15)] aspect-square flex flex-col active:scale-95"
            >
              <div className="relative flex-[75] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_936c1473-1350-488d-a877-d3aeedf41e88/artifacts/r55ll0px_Student%20Offer%20Zone%20%21%282%29.png"
                  alt="Student Offers"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" decoding="async"/>
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 w-10 h-10 sm:w-12 sm:h-12 bg-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Tag className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
              <div className="flex-[25] p-3 sm:p-4 bg-[#0a0e17] border-t border-white/10 flex flex-col justify-center">
                <h3 className="text-[11px] sm:text-lg md:text-xl font-bold text-white mb-0.5 sm:mb-1 font-outfit leading-tight">Student Offers</h3>
                <p className="text-[9px] sm:text-sm md:text-base text-gray-400 font-outfit line-clamp-2 leading-snug">Exclusive deals for students</p>
              </div>
            </div>

            {/* Events card */}
            <div
              onClick={() => navigate('/events')}
              data-testid="events-card"
              className="group cursor-pointer hover:scale-[1.02] bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-500 border border-white/8 hover:border-orange-500/30 hover:shadow-[0_20px_60px_rgba(249,115,22,0.15)] aspect-square flex flex-col active:scale-95"
            >
              <div className="relative flex-[75] overflow-hidden bg-gradient-to-br from-orange-500/20 via-amber-500/20 to-pink-500/20 flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_936c1473-1350-488d-a877-d3aeedf41e88/artifacts/vha02psa_Time%2830%29.jpg"
                  alt="Events at Nerd Nation — workshops, webinars and meetups"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg z-10">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
              <div className="flex-[25] p-3 sm:p-4 bg-[#0a0e17] border-t border-white/10 flex flex-col justify-center">
                <h3 className="text-[11px] sm:text-lg md:text-xl font-bold text-white mb-0.5 sm:mb-1 font-outfit leading-tight">Events</h3>
                <p className="text-[9px] sm:text-sm md:text-base text-gray-400 font-outfit line-clamp-2 leading-snug">Workshops, webinars & more</p>
              </div>
            </div>
          </div>
        </section>

        {/* Other Tools - RESPONSIVE: 1 col mobile, 2 cols tablet+ */}
        {/* Hide entire "More" section on mobile app */}
        {!isMobileApp() && (
          <section className="mb-8 sm:mb-10 md:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-5 sm:mb-6 flex items-center justify-start gap-2 sm:gap-3 font-playfair">
              <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-green-400 flex-shrink-0" />
              <span>More</span>
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
              {otherTools
                .filter(item => !(isMobileApp() && item.id === 'pricing')) // Hide Price Plans on mobile app
                .map((item) => (
                  <SquareFeatureCard key={item.id} item={item} />
                ))
              }
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;
